import { t } from 'i18next';
import TimeAgo from 'react-timeago';
import { Link } from 'react-router-dom';
import { useRef, useMemo, useState, useEffect, useCallback } from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Avatar from '@mui/material/Avatar';
import Tooltip from '@mui/material/Tooltip';
import Popover from '@mui/material/Popover';
import ListItem from '@mui/material/ListItem';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';

import { paths } from 'src/routes/paths';

import { useBoolean } from 'src/hooks/use-boolean';
import { useAuthContext } from 'src/hooks/auth/use-auth-context';

import { useAppSelector } from 'src/store/store';
import { AccountRole } from 'src/services/organisation/organisation.enum';
import { EmailParticipant } from 'src/services/email-sync/email-sync.types';
import { Comment, CommentType, ICommentData, CommentActivityType } from 'src/services/threads/threads.types';

import Label from 'src/components/label';
import Iconify from 'src/components/iconify';
import DateDisplay from 'src/components/date/date-display';
import TruncatedTextField from 'src/components/truncate-text/truncated-text-field';

// ----------------------------------------------------------------------

type Props = {
  comment: Comment;
  onEdit?: () => void;
  onDelete?: () => void;
};

export default function PostCommentItem({
  comment,
  onEdit,
  onDelete
}: Props) {

  const reply = useBoolean();

  const authContext = useAuthContext();

  const getActivityIcon = (typeCase: CommentActivityType) => {
    switch (typeCase) {
      case CommentActivityType.GENERAL:
        return 'fluent:note-20-regular';
      case CommentActivityType.PHONE_CALL:
        return 'mage:phone-outgoing';
      case CommentActivityType.CALL_RECEIVED:
        return 'mage:phone-incoming';
      case CommentActivityType.MEETING:
        return 'famicons:calendar-outline';
      default:
        return 'fluent:note-20-regular';
    }
  }

  const name = useMemo(() => comment.account ? `${comment.account.first_name} ${comment.account.last_name}` : t('common.unknown'), [comment]);

  return (
    <ListItem
      sx={{
        p: 0,
        pt: 3,
        width: '100%',
        maxWidth: '100%',
        overflow: 'hidden',
        alignItems: 'flex-start'
      }}
    >
      <Avatar alt={name} src={undefined} sx={{ mr: 2, mt: .5, width: 32, height: 32 }} />

      <Stack
        flexGrow={1}
        sx={{
          pb: 3,
          width: '100%',
          maxWidth: '100%',
          overflow: 'hidden',
          borderBottom: (theme) => `solid 1px ${theme.palette.divider}`,
        }}
      >
        <Stack direction="row" alignItems="start" justifyContent="space-between" spacing={2}>
          <Stack direction="column" alignItems="start">

            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="subtitle2">
                {name}
              </Typography>
            </Stack>

            <Typography variant="caption" sx={{ color: 'text.disabled' }}>
              <DateDisplay date={new Date(comment.created_at)} format='DD MMM YYYY' variant='caption' /> | <TimeAgo date={new Date(comment.created_at)} />

              {
                comment.is_edited && (
                  <Tooltip title={t('threads.comments.last_edited', { time: new Date(comment.updated_at) })}>
                    <Box component="span" sx={{ fontWeight: 600, cursor: 'help', color: 'black' }}> ({t('threads.comments.edited')})</Box>
                  </Tooltip>
                )
              }
            </Typography>
          </Stack>

          <Stack direction="row" alignItems="center" spacing={1}>
            {comment.type === CommentType.ACTIVITY && (
              <Box component="span">
                <Label
                  startIcon={<Iconify icon={getActivityIcon(comment.data.activity?.type as CommentActivityType)} />}>
                  {t(`threads.form.activity_type.options.${comment.data.activity?.type}`)}
                </Label>
              </Box>
            )}

            {comment.type === CommentType.EMAIL && (
              <Box component="span">
                <Label startIcon={<Iconify icon="ic:twotone-mail" />}>
                  {t(`threads.form.email_participant_type.${comment.data.email?.type}`)}
                </Label>
              </Box>
            )}

            {comment.type === CommentType.MEETING && (
              <Stack direction="row" spacing={0.5} alignItems="center">

                <Link
                  to={paths.dashboard.notetaker.detail(comment.data.meeting?.meeting_id ?? '')}
                  target='_blank'
                  style={{ justifyContent: 'center', alignItems: 'center', display: 'flex' }}
                >
                  <Tooltip title={t('threads.tooltips.view_meeting')}>
                    <Iconify icon="eva:external-link-fill" />
                  </Tooltip>
                </Link>
                <Label startIcon={<Iconify icon="icon-park-twotone:online-meeting" />}>
                  {t(`threads.form.activity_type.options.meeting`)}
                </Label>
              </Stack>
            )}

            <Stack direction="row">

              {
                (
                  onEdit &&
                  [CommentType.ACTIVITY].includes(comment.type) &&
                  [AccountRole.Admin, AccountRole.Op_Manager].includes(authContext.account.org_role)) &&
                (
                  <Tooltip title={t('threads.comments.edit_comment')}>
                    <IconButton
                      size="small"
                      onClick={onEdit}
                    >
                      <Iconify icon="eva:edit-fill" width={16} height={16} />
                    </IconButton>
                  </Tooltip>
                )}

              {
                (
                  onDelete &&
                  [CommentType.TEXT, CommentType.ACTIVITY].includes(comment.type) &&
                  [AccountRole.Admin, AccountRole.Op_Manager].includes(authContext.account.org_role)) &&
                (
                  <Tooltip title={t('threads.comments.delete_comment')}>
                    <IconButton
                      size="small"
                      onClick={onDelete}
                      color='error'
                    >
                      <Iconify icon="eva:trash-2-outline" width={16} height={16} />
                    </IconButton>
                  </Tooltip>
                )
              }

            </Stack>
          </Stack>
        </Stack>

        <ItemBody type={comment.type} data={comment.data} />

        {reply.value && (
          <Box sx={{ mt: 2 }}>
            <TextField fullWidth autoFocus placeholder="Write comment..." />
          </Box>
        )}
      </Stack>

    </ListItem >
  );
}

// Email Address List Component
type EmailAddressListProps = {
  addresses: EmailParticipant[];
  label: string;
  icon: string;
};

// Email Attachments List Component
type EmailAttachmentsListProps = {
  attachments?: {
    id: string;
    filename: string;
    mime_type: string;
    public_path: string;
  }[]
};

function EmailAddressList({ addresses, label, icon }: EmailAddressListProps) {

  const [expanded, setExpanded] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  // Format address to display name and email
  // Hide system-generated placeholder addresses (migrated records with no real email) — show the name only.
  const isPlaceholderAddr = (e?: string) =>
    !e || e.endsWith('@placeholder.local') || e.endsWith('@jobtetris-import.com');
  const formatAddress = (participant: EmailParticipant) => {
    const hideEmail = isPlaceholderAddr(participant.email);
    if (participant.name) return hideEmail ? participant.name : `${participant.name} <${participant.email}>`;
    return hideEmail ? '' : participant.email;
  };

  // Check if content overflows one line
  useEffect(() => {
    const checkOverflow = () => {
      if (contentRef.current) {
        const element = contentRef.current;
        // Check if content width exceeds container width
        setIsOverflowing(
          element.scrollWidth > element.clientWidth ||
          element.scrollHeight > element.clientHeight
        );
      }
    };

    // Run after a small delay to ensure rendering is complete
    const timer = setTimeout(checkOverflow, 0);
    window.addEventListener('resize', checkOverflow);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', checkOverflow);
    };
  }, [addresses, expanded]);

  if (!addresses || addresses.length === 0) return null;

  return (
    <Stack direction="column" spacing={0.5} sx={{ width: '100%', maxWidth: '100%' }}>
      <Box sx={{
        display: 'flex',
        mb: 0.5,
        alignItems: 'flex-start',
        width: '100%',
        maxWidth: '100%',
        overflow: 'hidden'
      }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          width: '60px',
          minWidth: '60px',
          mt: 0.5,
          flexShrink: 0
        }}>
          <Iconify icon={icon} width={14} height={14} sx={{ mr: 0.5, color: 'text.secondary' }} />
          <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
            {t(`common.${label}`)}:
          </Typography>
        </Box>
        <Box sx={{
          flex: 1,
          display: 'flex',
          direction: 'row',
          overflow: 'hidden'
        }}>
          <Box
            ref={contentRef}
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: expanded ? 'normal' : 'nowrap',
              pr: isOverflowing ? 3 : 0,
              width: '100%'
            }}
          >
            {addresses.map((participant, index) => (
              <Typography
                key={`${label}-${index}`}
                variant="caption"
                component="span"
                sx={{
                  maxWidth: '100%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}
              >
                {formatAddress(participant)}
                {index < addresses.length - 1 && ', '}
              </Typography>
            ))}
          </Box>

          {isOverflowing && !expanded && (
            <Tooltip title={t('common.show_more')}>
              <IconButton
                size="small"
                onClick={() => setExpanded(true)}
              >
                <Iconify
                  icon="mdi:chevron-down"
                  width={16}
                  height={16}
                  sx={{ color: 'text.secondary' }}
                />
              </IconButton>
            </Tooltip>
          )}

        </Box>
      </Box>

      {expanded && (
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          width: '100%',
          mt: 1
        }}>
          <Tooltip title={t('common.show_less')}>
            <IconButton
              size="small"
              onClick={() => setExpanded(false)}
              sx={{
                p: 0.5,
                bgcolor: 'background.neutral',
                '&:hover': {
                  bgcolor: 'action.hover'
                }
              }}
            >
              <Iconify
                icon="mdi:chevron-up"
                width={16}
                height={16}
                sx={{ color: 'text.secondary' }}
              />
            </IconButton>
          </Tooltip>
        </Box>
      )}
    </Stack>
  );
}

// Email Attachments List Component
function EmailAttachmentsList({ attachments }: EmailAttachmentsListProps) {
  if (!attachments || attachments.length === 0) return null;

  const getAttachmentIcon = (mimeType: string) => {
    if (mimeType.startsWith('image/')) return 'mdi:file-image-outline';
    if (mimeType === 'application/pdf') return 'mdi:file-pdf-box';
    if (mimeType.startsWith('video/')) return 'mdi:file-video-outline';
    if (mimeType.startsWith('audio/')) return 'mdi:file-music-outline';
    if (mimeType === 'application/zip' || mimeType === 'application/x-zip-compressed') return 'mdi:folder-zip-outline';
    if (mimeType === 'application/vnd.ms-excel' || mimeType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') return 'mdi:file-excel-outline';
    if (mimeType === 'application/msword' || mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') return 'mdi:file-word-outline';
    if (mimeType === 'application/vnd.ms-powerpoint' || mimeType === 'application/vnd.openxmlformats-officedocument.presentationml.presentation') return 'mdi:file-powerpoint-outline';
    return 'mdi:file-document-outline';
  }

  return (
    <Box sx={{
      mt: 1,
      mb: 1,
      bgcolor: 'background.neutral',
      px: 2,
      py: 1.5,
      borderRadius: 1,
      width: '100%',
      maxWidth: '100%',
      overflow: 'hidden'
    }}>
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        mb: 1
      }}>
        <Iconify icon="mdi:attachment" width={16} height={16} sx={{ mr: 0.5, color: 'text.secondary' }} />
        <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
          {t('common.documents')}
        </Typography>
      </Box>

      <Box sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 1
      }}>
        {attachments.map((file, index) => (
          <Box
            key={`attachment-${index}`}
            sx={{
              display: 'flex',
              alignItems: 'center',
              bgcolor: 'background.paper',
              borderRadius: 1,
              px: 1,
              py: 0.5,
              border: '1px solid',
              borderColor: 'divider',
              '&:hover': {
                bgcolor: 'action.hover'
              }
            }}
          >
            <a
              href={file.public_path}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'flex',
                alignItems: 'center',
                color: 'inherit',
                textDecoration: 'none'
              }}
            >
              <Iconify
                icon={getAttachmentIcon(file.mime_type)}
                width={18}
                height={18}
                sx={{ mr: 0.5, color: 'primary.main' }}
              />
              <Typography
                variant="caption"
                sx={{
                  color: 'primary.main',
                  fontWeight: 500,
                  maxWidth: '180px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  '&:hover': {
                    textDecoration: 'underline'
                  }
                }}
              >
                {file.filename}
              </Typography>
            </a>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

// Component to render text with highlighted mentions
type MentionTextProps = {
  text: string;
  users: any[];
  sx?: any;
  variant?: any;
};

function MentionText({ text, users, sx, variant = 'body2' }: MentionTextProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [hoveredUser, setHoveredUser] = useState<any | null>(null);

  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>, user: any) => {
    setAnchorEl(event.currentTarget);
    setHoveredUser(user);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
    setHoveredUser(null);
  };

  const open = Boolean(anchorEl);

  // Parse text and create elements with mentions highlighted
  const renderTextWithMentions = useMemo(() => {
    // Build an array of mention positions found in the text
    const mentions: Array<{ start: number; end: number; user: any }> = [];

    // For each user, check if their full name appears after @ in the text
    users.forEach(user => {
      const fullName = `${user.first_name} ${user.last_name}`;
      const searchPattern = `@${fullName}`;

      // Find all occurrences of this user's mention in the text (case-insensitive)
      let searchIndex = 0;
      const lowerText = text.toLowerCase();
      const lowerPattern = searchPattern.toLowerCase();

      // eslint-disable-next-line no-cond-assign
      while ((searchIndex = lowerText.indexOf(lowerPattern, searchIndex)) !== -1) {
        mentions.push({
          start: searchIndex,
          end: searchIndex + searchPattern.length,
          user
        });
        searchIndex += searchPattern.length;
      }
    });

    // Sort mentions by start position
    mentions.sort((a, b) => a.start - b.start);

    // Build the parts array
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    let keyIndex = 0;

    mentions.forEach(mention => {
      // Add text before the mention
      if (mention.start > lastIndex) {
        parts.push(text.substring(lastIndex, mention.start));
      }

      // Add the mention (highlighted)
      parts.push(
        <Typography
          key={`mention-${keyIndex++}`}
          component="span"
          variant={variant}
          onMouseEnter={(e) => handlePopoverOpen(e, mention.user)}
          onMouseLeave={handlePopoverClose}
          sx={{
            color: 'primary.main',
            fontWeight: 'bold',
            cursor: 'pointer',
            '&:hover': {
              textDecoration: 'underline'
            }
          }}
        >
          {text.substring(mention.start, mention.end)}
        </Typography>
      );

      lastIndex = mention.end;
    });

    // Add remaining text
    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex));
    }

    return parts.length > 0 ? parts : [text];
  }, [text, users, variant]);

  return (
    <>
      <TruncatedTextField
        text={renderTextWithMentions}
        sx={{
          wordBreak: 'break-word',
          whiteSpace: 'pre-wrap',
          maxWidth: '100%',
          overflow: 'hidden'
        }}
        variant="body2"
        limit={3}
        mode="lines"
        showMoreEnabled
      />

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        sx={{
          pointerEvents: 'none',
        }}
        disableRestoreFocus
      >
        {hoveredUser && (
          <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar
              src={undefined}
              alt={`${hoveredUser.first_name} ${hoveredUser.last_name}`}
              sx={{ width: 40, height: 40 }}
            >
              {hoveredUser.first_name?.[0]}{hoveredUser.last_name?.[0]}
            </Avatar>
            <Stack>
              <Typography variant="subtitle2">
                {hoveredUser.first_name} {hoveredUser.last_name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {hoveredUser.email || ''}
              </Typography>
            </Stack>
          </Box>
        )}
      </Popover>
    </>
  );
}

function ItemBody({ type, data }: { type: CommentType, data: ICommentData }) {

  const users = useAppSelector((state) => state?.data?.users);

  const cleanText = useCallback((inputText: string = "") => inputText
    // 1) turn all &nbsp; / &bnsp; into a real space
    .replace(/&(nbsp|bnsp);/gi, " ")
    // 2) collapse consecutive spaces or tabs
    .replace(/[ \t\u00A0]+/g, " ")
    // 3) normalise line endings to \n first
    .replace(/\r\n?/g, "\n")
    // 4) trim whitespace at the start or end of every line
    .replace(/^[ \t]+|[ \t]+$/gm, "")
    // 5) collapse two-plus blank lines into one
    .replace(/\n{2,}/g, "\n")
    // 6) final overall trim
    .trim(), []);

  const textData = useMemo(() => {

    let text: string = "";

    if (type === CommentType.TEXT) {
      text = data.text as string;
    }

    if (type === CommentType.ACTIVITY) {
      text = data.activity?.text as string;
    }

    if (type === CommentType.EMAIL) {
      text = data.email?.content as string;
    }

    if (type === CommentType.MEETING) {
      text = data.meeting?.summary as string;
    }

    return cleanText(text);
  }, [type, data, cleanText]);


  const getItemBody = () => {
    switch (type) {
      case CommentType.TEXT:
        return (
          <Box sx={{ mt: 1 }}>
            <MentionText
              text={textData}
              users={users}
              sx={{
                wordBreak: 'break-word',
                whiteSpace: 'pre-wrap',
                maxWidth: '100%',
                overflow: 'hidden'
              }}
              variant="body2"
            />
          </Box>
        );
      case CommentType.ACTIVITY:
        return (
          <MentionText
            text={textData}
            users={users}
            sx={{
              wordBreak: 'break-word',
              whiteSpace: 'pre-wrap',
              maxWidth: '100%',
              overflow: 'hidden'
            }}
            variant="body2"
          />
        );
      case CommentType.EMAIL:
        return (
          <Stack spacing={1} sx={{ mt: 1, width: '100%', maxWidth: '100%' }}>
            {/* Email header section */}
            <Box sx={{
              mb: 1,
              bgcolor: 'background.neutral',
              px: 2,
              py: 1,
              borderRadius: 1,
              width: '100%',
              maxWidth: '100%',
              overflow: 'hidden'
            }}>
              {/* Use the EmailAddressList component for each type of address */}
              <EmailAddressList
                addresses={data.email?.from || []}
                label="from"
                icon="mdi:account-arrow-left"
              />
              <EmailAddressList
                addresses={data.email?.to || []}
                label="to"
                icon="mdi:account-arrow-right"
              />
              <EmailAddressList
                addresses={data.email?.cc || []}
                label="cc"
                icon="mdi:account-multiple"
              />
              <EmailAddressList
                addresses={data.email?.bcc || []}
                label="bcc"
                icon="mdi:account-multiple-outline"
              />
            </Box>

            {/* Email content with divider */}
            <Box sx={{
              pt: 1.5,
              width: '100%',
              maxWidth: '100%',
              overflow: 'hidden',
              position: 'relative'
            }}>

              {/* Handle empty content case */}
              {!textData || textData.trim() === '' ? (
                <Typography variant="body2" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
                  {t('common.no_content')}
                </Typography>
              ) : (
                <TruncatedTextField
                  text={textData}
                  sx={{
                    wordBreak: 'break-word',
                    whiteSpace: 'pre-wrap',
                    maxWidth: '100%',
                    overflow: 'hidden'
                  }}
                  variant="body2"
                  limit={3}
                  mode="lines"
                  showMoreEnabled
                />
              )}
            </Box>

            {/* Display attachments if they exist */}
            {data?.email?.attachments && (
              <EmailAttachmentsList attachments={data?.email?.attachments} />
            )}
          </Stack>
        );
      default:
        return (
          <Typography variant="body2" sx={{ mt: 1, wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
            {textData}
          </Typography>
        );

    }
  }

  return (
    <>
      {getItemBody()}
    </>

  )
}
